import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import DateTimeField from "components/Common/DateTimeField"
import { connect } from "react-redux"
import { Link, withRouter } from "react-router-dom"
import { Card, CardBody, Container, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import SearchBox from "components/Common/SearchBox"
import { getAdmins } from "./actions"
import { readAdminQuery, writeAdminQuery, adminDateError, ADMIN_SORT_OPTIONS } from "./listQuery"
import listColumns from "./listColumns"
import { canManageAdmins } from "./adminDisplay"
import AdminOperationDialog from "./AdminOperationDialog"
import AdminRoleGuide from "./AdminRoleGuide"
import toastr from "toastr"
import "../../../assets/scss/custom/pages/_clubAccounts.scss"
import "../../../assets/scss/custom/pages/_admins.scss"

const labels = { search: "Search", role: "Role", status: "Status", fromDate: "Account created from", toDate: "Account created to" }
const roles = [["", "All roles"], ["admin", "Admin"], ["superAdmin", "Super admin"]]
const statuses = [["", "Current accounts"], ["activated", "Activated"], ["pending", "Pending"], ["blocked", "Blocked"], ["deleted", "Deleted"], ["all", "All statuses"]]
const displayFilter = (key, value) => (key === "role" ? roles : key === "status" ? statuses : []).find(([id]) => id === value)?.[1] || value

export const AdminDirectory = ({ admins = {}, getAdmins, listLoading = false, listError = "", userDetails = {}, history, location }) => {
  const { filters, options } = useMemo(() => readAdminQuery(location.search), [location.search])
  const [searchText, setSearchText] = useState(filters.search || "")
  const [advanced, setAdvanced] = useState(Boolean(filters.fromDate || filters.toDate))
  const [selected, setSelected] = useState(null)
  const timer = useRef()
  const dateError = adminDateError(filters)
  const columns = listColumns({ listSearch: location.search, userDetails, onDelete: setSelected })
  useEffect(() => { if (!dateError) getAdmins(filters, options) }, [filters, options, dateError, getAdmins])
  useEffect(() => {
    clearTimeout(timer.current)
    setSearchText(filters.search || "")
    if (filters.fromDate || filters.toDate) setAdvanced(true)
  }, [filters])
  useEffect(() => () => clearTimeout(timer.current), [])
  const update = useCallback((nextFilters, nextOptions) => {
    const search = writeAdminQuery(nextFilters, nextOptions)
    if (search !== history.location.search) history.replace({ ...history.location, search })
  }, [history])
  const changeFilter = patch => {
    clearTimeout(timer.current)
    update({ ...filters, search: searchText, ...patch }, { ...options, page: 1 })
  }
  const changeOptions = patch => {
    clearTimeout(timer.current)
    update({ ...filters, search: searchText }, { ...options, ...patch,
      ...(searchText.trim() !== (filters.search || "") && { page: 1 }) })
  }
  const clear = () => {
    clearTimeout(timer.current)
    setSearchText("")
    update({}, { ...options, page: 1 })
  }
  const changeSearch = value => {
    setSearchText(value)
    clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const current = readAdminQuery(history.location.search)
      update({ ...current.filters, search: value }, { ...current.options, page: 1 })
    }, 400)
  }
  // the „×”: the whole list at once, without the typing delay
  const clearSearch = () => {
    clearTimeout(timer.current)
    setSearchText("")
    const current = readAdminQuery(history.location.search)
    update({ ...current.filters, search: "" }, { ...current.options, page: 1 })
  }
  const ready = !listLoading && !listError && !dateError && admins.totalResults != null
    && admins.queryKey === writeAdminQuery(filters, options)
  const total = Number(admins.totalResults) || 0
  const pages = Math.max(1, Math.ceil(total / options.limit))
  const rows = admins.results || []
  useEffect(() => {
    if (ready && admins.page !== options.page) update(filters, { ...options, page: admins.page })
  }, [ready, admins.page, filters, options, update])
  const first = rows.length ? (options.page - 1) * options.limit + 1 : 0
  const last = rows.length ? first + rows.length - 1 : 0
  const activeFilters = Object.entries(filters)
  const select = (key, title, values) => <div className="club-filter-field">
    <label htmlFor={"admin-" + key}>{title}</label>
    <select id={"admin-" + key} className="custom-select" value={filters[key] || ""}
      onChange={event => changeFilter({ [key]: event.target.value })}>
      {values.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
    </select>
  </div>
  return <div className="page-content"><Container fluid>
    <Breadcrumbs title="User Accounts" breadcrumbItem="Admins" />
    <Card className="clubs-directory-card admins-directory"><CardBody className="p-0">
      <div className="club-directory-toolbar">
        <div className="club-directory-heading">
          <div><h4 className="mb-1">Admins {ready && <span className="text-muted">({total.toLocaleString()})</span>}</h4>
            <p className="text-muted mb-0">Manage administrative accounts and their access.</p></div>
          {canManageAdmins(userDetails) && <Link className="btn btn-primary d-inline-flex align-items-center justify-content-center"
            to={{ pathname: "/accounts/admins/create", state: { adminListSearch: location.search } }}>Create admin</Link>}
        </div>
        <AdminRoleGuide />
        <div className="club-search-row">
          <SearchBox look="pill" className="club-search-box" aria-label="Search admins" value={searchText}
            onValueChange={changeSearch} onClear={clearSearch} maxLength={120} placeholder="Search name, #ID or email" />
          <button type="button" className="btn btn-light club-more-filters" aria-expanded={advanced}
            aria-controls="admin-date-filters" onClick={() => setAdvanced(value => !value)}>
            <i className="bx bx-slider-alt mr-1" aria-hidden="true" />More filters
          </button>
        </div>
        <div className="club-filters-primary">
          {select("role", "Role", roles)}{select("status", "Status", statuses)}
          <div className="club-filter-field"><label htmlFor="admin-sort">Sort admins</label>
            <select id="admin-sort" className="custom-select" value={options.sortBy}
              onChange={event => changeOptions({ sortBy: event.target.value, page: 1 })}>
              {ADMIN_SORT_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select></div>
        </div>
        {advanced && <div className="club-filters-secondary" id="admin-date-filters">
          {[["fromDate", "Account created from"], ["toDate", "Account created to"]].map(([key, label]) =>
            <div className="club-filter-field" key={key}><label htmlFor={"admin-" + key}>{label}</label>
              <DateTimeField id={"admin-" + key} mode="date" value={filters[key] || ""} zoneLabel="Europe/Bucharest"
                {...(key === "fromDate" ? { rangeEnd: filters.toDate || "" } : { rangeStart: filters.fromDate || "" })}
                aria-invalid={Boolean(dateError)} aria-describedby={dateError ? "admin-date-error" : "admin-dates"}
                onChange={event => changeFilter({ [key]: event.target.value })} /></div>)}
        </div>}
        <p className="text-muted font-size-12 mt-2 mb-0" id="admin-dates">
          Current accounts include activated, pending and blocked accounts. Creation dates: Europe/Bucharest; both selected days are included.
        </p>
        {(activeFilters.length > 0 || searchText) && <div className="club-active-filters">
          {activeFilters.map(([key, value]) => <button key={key} type="button" className="club-filter-chip"
            aria-label={"Remove " + labels[key] + " filter"} onClick={() => changeFilter({ [key]: "" })}>
            {labels[key]}: {displayFilter(key, value)} <i className="bx bx-x" aria-hidden="true" /></button>)}
          <button type="button" className="btn btn-link btn-sm" onClick={clear}>Clear all</button>
        </div>}
      </div>
      {dateError ? <div className="admin-list-state text-danger" role="alert" id="admin-date-error">{dateError}</div>
        : listError ? <div className="admin-list-state" role="alert"><p>{listError}</p>
          <button type="button" className="btn btn-primary" onClick={() => getAdmins(filters, options)}>Retry admins</button></div>
          : !ready ? <div className="admin-list-state" role="status"><Spinner size="sm" className="mr-2" />Loading admins…</div>
            : !rows.length ? <div className="admin-list-state" role="status"><p>No admins match the current filters.</p>
              <span className="text-muted">Adjust the filters to include other accounts.</span></div>
              : <div className="table-responsive club-directory-table"><table className="table table-hover" aria-label="Admins">
                <thead className="thead-light"><tr>{columns.map(column => <th key={column.dataField} scope="col">{column.text}</th>)}</tr></thead>
                <tbody>{rows.map(row => <tr key={row.id}>{columns.map(column =>
                  <td key={column.dataField} data-label={column.text}>{column.formatter(row[column.dataField], row)}</td>)}</tr>)}</tbody>
              </table></div>}
      {ready && <div className="club-directory-footer">
        <div role="status" className="club-result-summary">{first}–{last} of {total.toLocaleString()} admins</div>
        <nav aria-label="Admin pages" className="admin-pagination">
          <button type="button" aria-label="Previous page" className="btn btn-light btn-sm" disabled={options.page <= 1}
            onClick={() => changeOptions({ page: options.page - 1 })}><i className="bx bx-chevron-left" aria-hidden="true" /></button>
          <span>Page {options.page} of {pages}</span>
          <button type="button" aria-label="Next page" className="btn btn-light btn-sm" disabled={options.page >= pages}
            onClick={() => changeOptions({ page: options.page + 1 })}><i className="bx bx-chevron-right" aria-hidden="true" /></button>
        </nav>
        <label className="club-page-size mb-0">Rows<select className="custom-select custom-select-sm" value={options.limit}
          onChange={event => changeOptions({ limit: Number(event.target.value), page: 1 })}>
          {[25, 50, 100].map(value => <option key={value}>{value}</option>)}</select></label>
      </div>}
    </CardBody></Card>
    {selected && <AdminOperationDialog account={selected} onClose={() => setSelected(null)} onSuccess={() => {
      setSelected(null)
      toastr.success("Admin account deleted.")
      getAdmins(filters, options)
    }} />}
  </Container></div>
}
const mapState = ({ admins = {}, session = {} }) => ({
  admins: admins.admins, listLoading: admins.listLoading, listError: admins.listError, userDetails: session.userDetails,
})
export default connect(mapState, { getAdmins })(withRouter(AdminDirectory))
